import React, { useCallback, useState } from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { fetchSwotScores } from 'apis/SwotAPI';
import { SWOT_CATEGORIES } from 'common/Widgets/constants';
import Modal from 'components/lib/Modal';
import { useAppDispatch } from 'store/hook';
import { handleSetCompetitorSwotScores } from 'store/slices/globalSlice';
import { CompetitorSwotScores } from 'types/actors';

import { EditSwotDataModalContext } from './EditSwotDataModalContext';
import EditSwotDataModalFooter from './EditSwotDataModalFooter';
import EditSwotDataModalHeader from './EditSwotDataModalHeader';
import StepSelector from './StepSelector';
import SwotCategories from './SwotCategories';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledModal = styled(Modal)`
  .ant-btn-primary:disabled {
    opacity: 0.3;
    color: #fff;
    background: #275aff;
  }


  &&& {
    .ant-modal-body {
      padding: 0px;
    }
`;

type Props = {
  open: boolean;
  competitorId: number | null;
  onClose: () => void;
};

const EditSwotDataModal = ({ open, onClose, competitorId }: Props) => {
  const dispatch = useAppDispatch();
  const [stepNumber, setStepNumber] = useState(1);
  const [isEditMode, setEditMode] = useState(false);
  const [temporarySwotState, setTemporarySwotState] = useState({});
  const selectedCategory = SWOT_CATEGORIES[stepNumber - 1];

  useQuery({
    queryKey: ['competitorSwotScores', competitorId],
    queryFn: () => fetchSwotScores(competitorId),
    onSuccess: (data) => {
      if (data && competitorId && !Object.keys(temporarySwotState).length) {
        setEditMode(true);
        data.created_by_full_name = data?.created_by?.full_name;
        data.updated_by_full_name = data?.updated_by?.full_name;
        dispatch(handleSetCompetitorSwotScores({ competitorId, scores: data }));
        setTemporarySwotState(data);
      }
    },
    refetchOnWindowFocus: false,
    enabled: !!competitorId,
  });

  const updateTemporaryState = useCallback(
    (scores: CompetitorSwotScores) => {
      setTemporarySwotState({ ...temporarySwotState, ...scores });
    },
    [setTemporarySwotState, temporarySwotState]
  );

  return (
    <EditSwotDataModalContext.Provider
      value={{
        competitorId,
        isEditMode,
        stepNumber,
        setStepNumber,
        temporarySwotState,
        setTemporarySwotState: updateTemporaryState,
        onClose,
      }}
    >
      <StyledModal
        open={open}
        closable
        destroyOnClose
        onCancel={onClose}
        footer={<EditSwotDataModalFooter />}
        width={1040}
      >
        <Container>
          <EditSwotDataModalHeader competitorId={competitorId} />
          <StepSelector stepTitle={selectedCategory.label} />
          <SwotCategories selectedCategory={selectedCategory} />
        </Container>
      </StyledModal>
    </EditSwotDataModalContext.Provider>
  );
};

export default EditSwotDataModal;
